import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { CKEditor } from '@ckeditor/ckeditor5-react';
import { InterfirstEditor } from '@lender/ckeditor-custom-build';
import { Box, Divider, useTheme } from '@mui/material';
import AppThemeProvider from 'app/providers/theme';
import PrismPagelessEditor from 'app/shared/components/forms/PrismPagelessEditor';
import useIsPrismPagelessEditorEnabled from 'app/shared/components/forms/PrismPagelessEditor/hooks/useIsPrismPagelessEditorEnabled';
import InlineAlertBanner from 'app/shared/componentsRestyled/common/InlineAlertBanner';
import clsx from 'clsx';
import _ from 'lodash';
import { nanoid } from 'nanoid';
import PropTypes from 'prop-types';
import { createRoot } from 'react-dom/client';
import Provider from 'react-redux/es/components/Provider';

import { PrismEditorBlockReason } from 'constants/forms';
import { StatusEnum } from 'constants/status';
import { useAsyncStateSafeStrictMode } from 'hooks/useAsyncStateSafeStrictMode';

import store from 'store/global';

import { PrismBlockMessage } from './FormEditor.constants';
import {
  blockedNoticeStyles,
  DOCUMENT_LIST_HEIGHT,
  DOCUMENT_LIST_WIDTH,
  getStyles,
} from './FormEditor.styles';
import { FormEditorTestIds } from './FormEditor.testIds';
import { registerToolbarHandlers } from './FormEditor.utils';
import FormEditorHeader from './FormEditorHeader';
import useIsPrismFormEditorEnabled from './hooks/useIsPrismFormEditorEnabled';
import useIsPrismFormEditorRequested from './hooks/useIsPrismFormEditorRequested';
import PrismFormEditor from './PrismFormEditor';
import ReactRenderingContent from './reactRendering';
import { removeTagByParentId } from './signature/Signature.utils';

const defaultObject = {};

const defaultForm = {};
const rootCache = new WeakMap();

const FormEditor = ({
  editorType = InterfirstEditor,
  value = '',
  onChange = _.noop,
  className = '',
  readOnly = false,
  isEditMode = false,
  header,
  formActions = null,
  form = defaultForm,
  isTemplateSavedInPrism = false,
  prismBlockReason = null,
  config = defaultObject,
  isEditorHeaderAllowed = true,
  isLegacyTemplateHtml = false,
  initialDocumentJson = null,
  onDocumentChange = _.noop,
  onExportPdf = null,
  prismEditorRef = null,
  ariaLabel = '',
  autoFocus = false,
  disabled = false,
  error = false,
  minHeight = null,
  'data-testid': dataTestId = null,
}) => {
  const theme = useTheme();
  const classes = getStyles(theme, { isEditorHeaderAllowed });
  const toolbarContainerRef = useRef(null);
  const isDocumentEditor = editorType === InterfirstEditor;
  const isPrismEnabledForDocuments = useIsPrismFormEditorEnabled(
    isTemplateSavedInPrism,
    prismBlockReason,
  );
  const isPrismFormEditorEnabled = isPrismEnabledForDocuments && isDocumentEditor;
  const isPrismRequested = useIsPrismFormEditorRequested(isTemplateSavedInPrism);
  const isPrismPagelessFieldEnabled = useIsPrismPagelessEditorEnabled() && !isDocumentEditor;
  const isBlockedFromEditors = isDocumentEditor && !!prismBlockReason && isPrismRequested;

  const [editor, setEditor] = useAsyncStateSafeStrictMode(null);

  const ckeditorConfig = useMemo(
    () => ({
      width: DOCUMENT_LIST_WIDTH,
      height: DOCUMENT_LIST_HEIGHT,
      reactRendering: {
        renderFunction: ({ domElement, ...props }) => {
          const cachedRoot = rootCache.get(domElement);
          const root = cachedRoot || createRoot(domElement);

          if (!cachedRoot) {
            rootCache.set(domElement, root);
          }

          root.render(
            <Provider store={store}>
              <AppThemeProvider.CDS>
                <ReactRenderingContent {...props} readOnly={readOnly} />
              </AppThemeProvider.CDS>
            </Provider>,
          );
        },
      },
      ...config,
    }),
    [config, readOnly],
  );

  useEffect(() => {
    if (!isEditMode) {
      return;
    }

    import('juice');
  }, [isEditMode]);

  const onChangeInternalState = useCallback(
    (e, editor) => {
      const changes = e.source.differ.getChanges();
      const removedTag = changes[changes.length - 1];
      const dataSigningId =
        removedTag?.type === 'remove' && removedTag?.attributes?.get('dataSigningId');
      const data = editor.getData();
      const resultData = dataSigningId ? removeTagByParentId(data, dataSigningId) : data;

      onChange(resultData);
    },
    [onChange],
  );

  useEffect(() => {
    if (!editor || !toolbarContainerRef.current || readOnly) {
      return;
    }

    const toolbarElement = editor.ui.view.toolbar.element;

    toolbarContainerRef.current.appendChild(toolbarElement);
    const cleanupFunction = registerToolbarHandlers(toolbarContainerRef);

    return () => {
      cleanupFunction();
      toolbarElement.remove();
    };
  }, [readOnly, editor]);

  const onReady = useCallback(
    editor => {
      if (!editor) {
        return;
      }

      setEditor(editor);

      if (readOnly) {
        const lockId = nanoid();
        editor.enableReadOnlyMode(lockId);
      }
    },
    [setEditor, readOnly],
  );

  const onError = useCallback(
    (error, { willEditorRestart } = {}) => {
      // If the editor is restarted, the toolbar element will be created once again.
      // The `onReady` callback will be called again and the new toolbar will be added.
      // This is why you need to remove the older toolbar.
      if (willEditorRestart && !readOnly) {
        editor.ui.view.toolbar.element.remove();
      }
    },
    [editor, readOnly],
  );

  if (isBlockedFromEditors) {
    return (
      <Box className={clsx(className, 'h-full', 'flex', 'flex-col')} sx={classes.mainContainer}>
        {isEditorHeaderAllowed && (
          <FormEditorHeader
            form={form}
            formActions={formActions}
            header={header}
            isEditMode={isEditMode}
          />
        )}
        <Divider variant="fullWidth" />
        <Box sx={blockedNoticeStyles}>
          <InlineAlertBanner
            dataTestId={FormEditorTestIds.BLOCKED_NOTICE}
            message={PrismBlockMessage[prismBlockReason]}
            variant={StatusEnum.INFO}
          />
        </Box>
      </Box>
    );
  }

  if (isPrismPagelessFieldEnabled) {
    return (
      <PrismPagelessEditor
        ariaLabel={ariaLabel}
        autoFocus={autoFocus}
        className={className}
        data-testid={dataTestId}
        disabled={disabled}
        error={error}
        minHeight={minHeight}
        readOnly={readOnly}
        value={value}
        onChange={onChange}
      />
    );
  }

  if (isPrismFormEditorEnabled) {
    return (
      <PrismFormEditor
        className={className}
        form={form}
        formActions={formActions}
        header={header}
        initialDocumentJson={initialDocumentJson}
        isEditMode={isEditMode}
        isEditorHeaderAllowed={isEditorHeaderAllowed}
        isLegacyTemplateHtml={isLegacyTemplateHtml}
        prismEditorRef={prismEditorRef}
        readOnly={readOnly}
        value={value}
        onChange={onChange}
        onDocumentChange={onDocumentChange}
        onExportPdf={onExportPdf}
      />
    );
  }

  return (
    <Box className={clsx(className, 'h-full', 'flex', 'flex-col')} sx={classes.mainContainer}>
      {isEditorHeaderAllowed && (
        <FormEditorHeader
          form={form}
          formActions={formActions}
          header={header}
          isEditMode={isEditMode}
        />
      )}

      <Divider variant="fullWidth" />

      <Box sx={classes.editor}>
        {!readOnly && <Box ref={toolbarContainerRef} sx={classes.toolbarContainer} />}
        <Box className={readOnly ? 'read-only' : ''} sx={classes.editorContainer}>
          <CKEditor
            config={ckeditorConfig}
            data={value}
            editor={editorType}
            onChange={onChangeInternalState}
            onError={onError}
            onReady={onReady}
          />
        </Box>
      </Box>
    </Box>
  );
};

FormEditor.propTypes = {
  header: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  form: PropTypes.object,
  isTemplateSavedInPrism: PropTypes.bool,
  prismBlockReason: PropTypes.oneOf(Object.values(PrismEditorBlockReason)),
  editorType: PropTypes.func,
  value: PropTypes.string,
  className: PropTypes.string,
  readOnly: PropTypes.bool,
  isEditorHeaderAllowed: PropTypes.bool,
  isLegacyTemplateHtml: PropTypes.bool,
  formActions: PropTypes.node,
  config: PropTypes.object,
  isEditMode: PropTypes.bool,
  initialDocumentJson: PropTypes.object,
  prismEditorRef: PropTypes.shape({ current: PropTypes.object }),
  ariaLabel: PropTypes.string,
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  minHeight: PropTypes.number,
  'data-testid': PropTypes.string,
  onChange: PropTypes.func,
  onDocumentChange: PropTypes.func,
  onExportPdf: PropTypes.func,
};

export default FormEditor;
